import React from "react"
import { Menu } from "dash-styled"
import identity from "lodash/identity"
import {
    FilterFields,
    stringFilter
} from "../../../../../Common/FilterComposer/FilterComposer"
import {
    FilterExpression,
    UnaryFilterOperator
} from "../../../../../../Models/Client/FilterExpression"
import { TableColumn } from "resha"
import {
    APIWorker,
    getWorkerDisplayText
} from "../../../../../../Models/API/Worker"
import { SortOptionField } from "../../../../../Common/Button/SortChooser"
import { ReactComponent as IconGear } from "icons/lib/gear.svg"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { APIWorkerGroup } from "../../../../../../Models/API/WorkerGroup"
import capitalize from "lodash/capitalize"
import { NoData } from "../../../../../Common/NoData/NoData"
import { getWorkerTypeDisplayText } from "../../../../../../Models/API/WorkerType"

export const workerSortOptions: SortOptionField<APIWorker>[] = [
    {
        field: "status",
        label: "Status",
        ascLabel: "Active first",
        descLabel: "Active last"
    },
    {
        field: "type",
        label: "Type",
        ascLabel: "Alphabetical",
        descLabel: "Reverse alphabetical"
    },
    {
        field: "created",
        label: "Created",
        ascLabel: "Oldest first",
        descLabel: "Youngest first"
    }
]

export const defaultWorkerSort: SortExpression<APIWorker> = {
    sortBy: "status",
    desc: false
}

export function workerColumns<T extends string>(
    menuItems: T[],
    myEmail: string,
    onSelectMenu: (selected: T, id: number) => void
): TableColumn<APIWorker>[] {
    return [
        {
            renderHeader: () => "Name",
            render: model => (
                <>
                    {model.type === "USER" ? model.userUsername : model.type}
                    {model.type === "USER" && model.userEmail === myEmail ? (
                        <span className="ml-1 italic">(you)</span>
                    ) : (
                        ""
                    )}
                </>
            )
        },
        {
            renderHeader: () => "Email",
            render: model =>
                model.type === "USER" ? model.userEmail : <NoData />
        },
        {
            renderHeader: () => "Type",
            render: ({ type }) => getWorkerTypeDisplayText(type)
        },
        {
            renderHeader: () => "Belongs to",
            render: ({ groupsTotal }) =>
                groupsTotal === 0 ? (
                    <NoData />
                ) : groupsTotal === 1 ? (
                    `${groupsTotal} group`
                ) : (
                    `${groupsTotal} groups`
                )
        },
        {
            renderHeader: () => "Status",
            render: ({ status }) => capitalize(status)
        },
        {
            className: "w-24",
            renderHeader: () => "Modify",
            render: ({ id }) => (
                <Menu
                    title={<IconGear className="w-4 h-4 -mt-px mr-3" />}
                    className="-my-1"
                    align="right"
                    nonInteractive={false}
                    items={menuItems}
                    onChoose={selected => onSelectMenu(selected, id)}
                />
            )
        }
    ]
}

export const defaultWorkerGroupSort: SortExpression<APIWorkerGroup> = {
    sortBy: "name",
    desc: false
}

export function workerGroupColumns<T extends string>(
    menuItems: T[],
    onSelectMenu: (selected: T, id: number) => void
): TableColumn<APIWorkerGroup>[] {
    return [
        {
            renderHeader: () => "Name",
            render: ({ name }) => name
        },
        {
            renderHeader: () => "Active members",
            render: ({ workers }) => {
                return workers.length === 0 ? (
                    <NoData />
                ) : (
                    `${workers
                        .slice(0, 2)
                        .map(getWorkerDisplayText)
                        .join(", ")}${
                        workers.length > 2
                            ? ` and ${workers.length - 2} more`
                            : ""
                    }`
                )
            }
        },
        {
            className: "w-24",
            renderHeader: () => "Modify",
            render: ({ id }) => (
                <Menu
                    title={<IconGear className="w-4 h-4 -mt-px mr-3" />}
                    className="-my-1"
                    align="right"
                    nonInteractive={false}
                    items={menuItems}
                    onChoose={selected => onSelectMenu(selected, id)}
                />
            )
        }
    ]
}

export const workerGroupSortFields: SortOptionField<APIWorkerGroup>[] = [
    {
        field: "name",
        label: "Name",
        ascLabel: "Alphabetical",
        descLabel: "Reverse alphabetical"
    },
    {
        field: "created",
        label: "Created",
        ascLabel: "Oldest first",
        descLabel: "Youngest first"
    }
]

export const workerGroupFilterFields: FilterFields<APIWorkerGroup> = {
    name: stringFilter<string>(
        "Name",
        identity,
        identity,
        "",
        UnaryFilterOperator.contains,
        "contains"
    )
}

export const workerGroupDefaultFilter: FilterExpression<APIWorkerGroup> = {
    fieldName: "name",
    operator: UnaryFilterOperator.contains,
    value: ""
}
